import React, { useState, useRef, useMemo } from 'react';
import { useEditor } from '../../context/EditorContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Type, Image, Ruler, Move, Trash2, Upload, X } from 'lucide-react';
import api from '../../services/api';
import { compressImageFile, MAX_UPLOAD_BYTES } from '../../utils/imageCompression';
import { logger } from '../../utils/logger';
import { PRINT_REGIONS } from '../../config/printAreas';

const getUploadErrorMessage = (err) => {
  if (err.response?.status === 413) return 'Image is too large. Please choose a smaller photo.';
  if (err.response?.data?.detail) return err.response.data.detail;
  if (err.message === 'Network Error') return 'Network error - the image may be too large. Please try a smaller photo.';
  return 'Upload failed. Please try again.';
};

// Size Selector Component
const SizeSelector = () => {
  const { size, setSize, TSHIRT_SIZES } = useEditor();
  
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {TSHIRT_SIZES.map((s) => (
          <button
            key={s}
            data-testid={`size-${s}`}
            onClick={() => setSize(s)}
            className={`py-2 px-2 rounded-lg text-sm font-bold transition-all ${
              size === s
                ? 'bg-primary text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};

// Simple Text Tool
const TextTool = () => {
  const { addElement, view, selectedElementId, getCurrentElements, updateElement } = useEditor();
  const [text, setText] = useState('Your Text');
  const [fontSize, setFontSize] = useState(24);
  const [textColor, setTextColor] = useState('#FFFFFF');
  const sliderValue = useMemo(() => [fontSize], [fontSize]);
  
  const handleAddText = () => {
    if (!text.trim()) return;
    const region = PRINT_REGIONS[view];
    addElement({
      type: 'text',
      text: text,
      x: region.x + region.width / 2 - 50,
      y: region.y + region.height / 2 - 15,
      width: 100,
      height: 30,
      fontSize,
      fill: textColor,
      fontFamily: 'Arial',
    });
  };
  
  const colors = ['#FFFFFF', '#000000', '#FF0000', '#0066FF', '#00CC66', '#FFD700'];
  
  return (
    <div className="space-y-2">
      {/* Text Input */}
      <Input
        data-testid="text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type your text..."
        className="h-10 text-sm rounded-lg border-2 focus:border-primary"
      />
      
      {/* Size and Color in one row */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <p className="text-[10px] text-gray-500 mb-1">Size: {fontSize}px</p>
          <Slider
            value={sliderValue}
            onValueChange={(val) => setFontSize(val[0])}
            min={12}
            max={48}
            step={2}
          />
        </div>
        <div className="flex gap-1.5">
          {colors.map((c) => (
            <button
              key={c}
              onClick={() => setTextColor(c)}
              className={`w-6 h-6 rounded-full transition-transform ${
                textColor === c ? 'scale-110 ring-2 ring-primary ring-offset-1' : ''
              }`}
              style={{ 
                backgroundColor: c,
                border: c === '#FFFFFF' ? '2px solid #ddd' : 'none'
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Add Button */}
      <Button
        data-testid="add-text-btn"
        onClick={handleAddText}
        disabled={!text.trim()}
        className="w-full h-10 text-sm font-bold rounded-lg bg-primary"
      >
        <Type className="w-4 h-4 mr-2" />
        Add Text to Shirt
      </Button>
    </div>
  );
};

// Simple Image Upload
const ImageUpload = () => {
  const { addElement, view } = useEditor();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset file input so the same file can be uploaded again
    if (e.target) {
      e.target.value = '';
    }
    if (!file) return;
    
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Image is too large (max 25MB). Please choose a smaller photo.');
      return;
    }
    
    setUploading(true);
    setError(null);
    
    try {
      // Compress large photos client-side so uploads stay under proxy limits
      const uploadFile = await compressImageFile(file);
      const response = await api.uploadFile(uploadFile);
      
      // Construct full URL for the uploaded image
      // Use relative URL if no backend URL specified - this ensures images work on deployed site
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const imageUrl = response.url.startsWith('http') 
        ? response.url 
        : (backendUrl ? `${backendUrl}${response.url}` : `${window.location.origin}${response.url}`);
      
      // Create image to get dimensions
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const region = PRINT_REGIONS[view];
        const maxSize = Math.min(region.width, region.height) * 0.8;
        const scale = maxSize / Math.max(img.width, img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        
        addElement({
          type: 'image',
          src: imageUrl,
          x: region.x + (region.width - w) / 2,
          y: region.y + (region.height - h) / 2,
          width: w,
          height: h,
        });
        setUploading(false);
      };
      
      img.onerror = () => {
        logger.error('Uploaded image could not be loaded for preview:', imageUrl);
        setError('The image could not be displayed. Please try a different photo (JPG or PNG works best).');
        setUploading(false);
      };
      
      img.src = imageUrl;
    } catch (err) {
      logger.error('Image upload failed:', err);
      setError(getUploadErrorMessage(err));
      setUploading(false);
    }
  };
  
  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        data-testid="image-upload-input"
      />
      
      <div 
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all active:bg-primary/10"
      >
        <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
        <p className="text-base font-semibold text-gray-700">Tap to upload image</p>
        <p className="text-xs text-gray-500 mt-1">Choose from gallery or camera</p>
      </div>
      
      {uploading && (
        <div className="text-center py-3">
          <div className="w-6 h-6 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-xs text-gray-500 mt-2">Uploading...</p>
        </div>
      )}
      
      {error && (
        <div className="text-center py-2 text-red-500 text-xs" data-testid="image-upload-error">
          {error}
        </div>
      )}
    </div>
  );
};

// Edit/Manage Designs
const ElementEditor = () => {
  const { 
    getCurrentElements, 
    selectedElementId, 
    setSelectedElementId, 
    removeElement,
    clearAllElements,
    view 
  } = useEditor();
  
  const elements = getCurrentElements();
  const viewLabel = view.charAt(0).toUpperCase() + view.slice(1);
  
  if (elements.length === 0) {
    return (
      <div className="text-center py-8">
        <Move className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">No designs on {viewLabel} yet</p>
        <p className="text-sm text-gray-400 mt-1">Add text or image first</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center mb-2">
        <p className="text-sm font-semibold text-gray-600">{elements.length} design(s) on {viewLabel}</p>
        <button 
          onClick={clearAllElements}
          className="text-red-500 text-sm font-medium"
        >
          Clear All
        </button>
      </div>
      
      {elements.map((el, index) => (
        <div 
          key={el.id}
          onClick={() => setSelectedElementId(el.id)}
          className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
            selectedElementId === el.id 
              ? 'border-primary bg-primary/5' 
              : 'border-gray-200 bg-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              el.type === 'text' ? 'bg-blue-100' : 'bg-green-100'
            }`}>
              {el.type === 'text' ? <Type className="w-5 h-5 text-blue-600" /> : <Image className="w-5 h-5 text-green-600" />}
            </div>
            <div>
              <p className="font-semibold text-gray-800">
                {el.type === 'text' ? `"${el.text?.substring(0, 15)}${el.text?.length > 15 ? '...' : ''}"` : 'Image'}
              </p>
              <p className="text-xs text-gray-500">{el.type === 'text' ? `${el.fontSize}px` : 'Photo'}</p>
            </div>
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeElement(el.id);
            }}
            className="w-10 h-10 rounded-full bg-red-100 text-red-500 flex items-center justify-center hover:bg-red-200"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      ))}
      
      <p className="text-xs text-gray-500 text-center mt-4">
        Tap design on shirt to select • Drag to move • Corners to resize
      </p>
    </div>
  );
};

// Main Mobile Controls Component
export const EditorControls = ({ isMobile = false, onClose }) => {
  return (
    <div className={`bg-white ${isMobile ? 'rounded-t-3xl shadow-2xl max-h-[60vh]' : 'rounded-2xl shadow-xl'} p-3 sm:p-4`}>
      {/* Header with close button for mobile */}
      {isMobile && (
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
          <span className="text-sm font-bold text-gray-700">Add Your Design</span>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      )}
      
      <Tabs defaultValue="text" className="w-full">
        <TabsList className="w-full grid grid-cols-4 bg-gray-100 rounded-xl p-1 mb-3 h-11">
          <TabsTrigger 
            value="size" 
            className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white font-semibold h-full text-xs"
            data-testid="tab-size"
          >
            <Ruler className="w-4 h-4" />
          </TabsTrigger>
          <TabsTrigger 
            value="text" 
            className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white font-semibold h-full text-xs"
            data-testid="tab-text"
          >
            <Type className="w-4 h-4" />
          </TabsTrigger>
          <TabsTrigger 
            value="image" 
            className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white font-semibold h-full text-xs"
            data-testid="tab-image"
          >
            <Image className="w-4 h-4" />
          </TabsTrigger>
          <TabsTrigger 
            value="edit" 
            className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white font-semibold h-full text-xs"
            data-testid="tab-edit"
          >
            <Move className="w-4 h-4" />
          </TabsTrigger>
        </TabsList>
        
        <div className={`${isMobile ? 'max-h-[calc(60vh-100px)] overflow-y-auto' : 'min-h-[200px]'}`}>
          <TabsContent value="size" className="mt-0">
            <SizeSelector />
          </TabsContent>
          
          <TabsContent value="text" className="mt-0">
            <TextTool />
          </TabsContent>
          
          <TabsContent value="image" className="mt-0">
            <ImageUpload />
          </TabsContent>
          
          <TabsContent value="edit" className="mt-0">
            <ElementEditor />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default EditorControls;
